import useFrontendBoot from "@/Hooks/useFrontendBoot";
import FullPageLoader from "@/UI/Loaders/FullPageLoader";
import "@/styles/pattern.css";
import { RGX_email } from "@/utility/regexs";
import { ArrowLongRightIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { Image, TextInput, ButtonBase } from "@repo/ui/index.tsx";
import { FormProvider, useForm } from "react-hook-form";
import { ILoginSchema } from "./auth.types";
import useLogin from "./hooks/useLogin";
import useAuthentication from "@/Hooks/useAuthentication";
import { Helmet } from "react-helmet-async";
import { useCaptchaService } from "@repo/api-services/admin";
import { inputUtility } from "@repo/utilities/input.utility.ts";

function Login() {
  const {
    services: { authService },
  } = useAuthentication();
  const {
    mutations: { loginMutation },
    functions: { handleLogin },
  } = useLogin();
  const { logo, frontendBootService } = useFrontendBoot();
  const {
    services: { captchaService },
  } = useCaptchaService({});
  const generalSettingsData =
    frontendBootService?.data?.data?.settings?.general || {};
  const formData = useForm<ILoginSchema>();

  // Function to handle form submission
  const onSubmitHandler = async (data: ILoginSchema) => {
    const res = await handleLogin({
      ...data,
      device_name: "web",
      captcha_key: captchaService?.data?.captcha_key,
    });
    if (res) formData.setValue("captcha", "");
  };

  if (authService.isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="h-screen w-full flex items-start ">
      {generalSettingsData && (
        <Helmet>
          <link
            rel="icon"
            type="image/svg+xml"
            href={generalSettingsData?.favicon?.original_url}
          />
          <title>{generalSettingsData?.meta_title}</title>
          <meta
            name="description"
            content={generalSettingsData?.meta_description}
          />
        </Helmet>
      )}
      <section id="pattern_banknote" className="flex-1 flex h-full">
        <div className="flex-1 flex items-center justify-center pl-10 bg-gradient-to-br from-primary/20 via-transparent to-transparent relative">
          <h1 className="text-[3rem] font-black text-heading leading-snug">
            Enhance Your Coverage With{" "}
            <span className="font-serif font-black text-[4.5rem] text-primary  italic">
              Affinity
            </span>
            Insurance for Tailored Protection.
          </h1>
        </div>
      </section>
      <section className="w-2/5 h-full flex justify-center items-center">
        <FormProvider {...formData}>
          <form
            onSubmit={formData.handleSubmit(onSubmitHandler)}
            className="w-96 flex flex-col gap-4"
          >
            {logo ? (
              <Image
                src={logo}
                alt="company logo"
                className="w-56 max-h-24 p-3 object-contain border-2 border-primary/10 rounded-lg"
              />
            ) : (
              <div className="h-[3.5rem] rounded-lg w-[10rem] bg-extraLightGray animate-pulse" />
            )}

            <h2 className="text-lg font-semibold">Welcome back!</h2>
            <TextInput
              name="email"
              type="text"
              placeholder="Enter email..."
              label="Email Address"
              registerOptions={{
                required: "Email is required",
                pattern: {
                  value: RGX_email,
                  message: "Invalid email address",
                },
              }}
              disableCopy
              {...(!import.meta.env.DEV && {
                disablePaste: true,
              })}
            />
            <div className="grid gap-2">
              <TextInput
                name="password"
                type="password"
                placeholder="Enter password..."
                label="Password"
                registerOptions={{
                  required: "Password is required",
                }}
                {...(!import.meta.env.DEV && {
                  disablePaste: true,
                })}
                disableCopy
              />
              <ButtonBase
                buttonType="tertiary"
                text="Forgot password!"
                className="!block text-primary text-sm self-end text-end w-full"
              />
              <div className="flex w-full gap-2 ">
                <div className="w-1/2">
                  <Image
                    src={captchaService?.data?.captcha_image}
                    alt="captcha logo"
                    className="h-10 w-full object-fill border border-lightGray rounded-lg"
                  />
                </div>
                <div className="w-1/2">
                  <TextInput
                    name="captcha"
                    placeholder="Enter captcha..."
                    registerOptions={{
                      required: "Captcha is required",
                      onChange: (e) =>
                        inputUtility.onlyAlphabetsAndNumbers(e, 10),
                    }}
                  />
                </div>
                <div className="pt-1">
                  <ButtonBase
                    icon={
                      <ArrowPathIcon
                        className={`size-5 ${captchaService?.isFetching ? "animate-spin" : ""}`}
                      />
                    }
                    className="size-7 text-body"
                    buttonType="tertiary"
                    onClick={() => {
                      captchaService.refetch?.();
                      formData.setValue("captcha", "");
                    }}
                  />
                </div>
              </div>
            </div>
            <ButtonBase
              type="submit"
              text="Login"
              buttonType="primary"
              icon={<ArrowLongRightIcon className="w-8" />}
              isLoading={loginMutation.isPending}
            />
          </form>
        </FormProvider>
      </section>
    </div>
  );
}

export default Login;
